/**
 * @file useDropdownPosition.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-31
 * @modified 2026-08-27
 * @description Hook to detect available viewport space and flip dropdown menus
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Properties that make an element the containing block for `position: fixed`
 * descendants. A fixed box is normally positioned against the viewport — but any
 * ancestor carrying one of these takes that job over, and the coordinates are
 * then read against ITS box instead.
 *
 * This is not a corner case here: `.content-inner` wraps every page in the
 * dashboard and carries `backdrop-filter: blur(5px)` on all of them bar home.
 * So every escapeOverflow menu in the app is positioned against that element,
 * never the viewport, and viewport coordinates land it low and to the right by
 * however far the panel sits from the screen's corner — roughly the sidebar's
 * width across and the header's height down.
 */
const CB_PROPS = ['transform', 'perspective', 'filter', 'backdropFilter', 'webkitBackdropFilter'];
const CB_CONTAIN = /(^|\s)(layout|paint|strict|content)(\s|$)/;
const CB_WILL_CHANGE = /transform|perspective|filter|contain/;

function establishesFixedContainingBlock(cs) {
  if (CB_PROPS.some((p) => cs[p] && cs[p] !== 'none')) return true;
  if (cs.willChange && CB_WILL_CHANGE.test(cs.willChange)) return true;
  if (cs.contain && CB_CONTAIN.test(cs.contain)) return true;
  if (cs.containerType && cs.containerType !== 'normal') return true;
  return false;
}

/**
 * The fixed-position box for a menu hung off `rect`, expressed in whatever frame
 * actually applies.
 *
 * `frame` is the containing block's padding box, or null when the viewport is
 * the containing block — in which case the frame is the viewport and the offsets
 * are zero, which is what the plain object below reduces to.
 *
 * Exported because it is pure arithmetic in a hook that otherwise needs a DOM:
 * the offsets are easy to get subtly wrong and the wrongness is invisible until
 * someone opens a dropdown on a page laid out differently.
 */
/** The gap an aligned menu keeps from the edge it is pinned to. */
const ALIGN_INSET_DESKTOP = 20;
const ALIGN_INSET_MOBILE = 10;
const MOBILE_MAX_WIDTH = 768;

const alignInset = () =>
  (typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH)
    ? ALIGN_INSET_MOBILE : ALIGN_INSET_DESKTOP;

export function menuBoxFor({
  rect, frame, flipUp, viewportHeight, viewportWidth,
  align = null, widthRect = null
}) {
  const left = frame ? frame.left : 0;
  const top = frame ? frame.top : 0;
  const bottom = frame ? frame.bottom : viewportHeight;
  const frameRight = frame ? frame.right : (viewportWidth ?? 0);

  const vertical = flipUp
    ? { bottom: bottom - rect.top + 4, top: 'auto' }
    : { top: rect.bottom - top + 4, bottom: 'auto' };

  /*
   * Aligned to one edge of a NAMED element and sized to it, rather than to the
   * trigger.
   *
   * A filter in a dashboard card is narrow, and a menu forced to the trigger's
   * width wraps every option onto three lines. Sized to the card's content
   * width instead — its box less the same inset on both sides — so both filters
   * on a card get identical menus and neither can overhang it.
   *
   * The two alignments land in the same place while the width is the full
   * content width, which is the intent: each menu is still pinned to its own
   * side, so if anything ever clamps the width they open away from their edge
   * rather than both drifting the same way.
   */
  if (align) {
    const inset = alignInset();
    const box = widthRect || rect;
    const width = Math.max(120, box.width - inset * 2);
    const horizontal = align === 'right'
      ? { right: frameRight - box.right + inset, left: 'auto' }
      : { left: box.left - left + inset, right: 'auto' };
    return { position: 'fixed', minWidth: width, maxWidth: width, ...horizontal, ...vertical };
  }

  return { position: 'fixed', left: rect.left - left, width: rect.width, ...vertical };
}

/**
 * Returns positioning classes for a dropdown menu based on available space
 * within its nearest scrollable ancestor (falling back to the viewport).
 * @param {React.RefObject} containerRef - ref to the dropdown container element
 * @param {boolean} isOpen - whether the dropdown is currently open
 * @param {{ reflowOnScroll?: boolean }} [options]
 *   reflowOnScroll — re-evaluate the flip direction as the scroll ancestor
 *   scrolls (and flip up only when the menu's measured height would actually
 *   overflow that ancestor's bottom edge, rather than using a fixed threshold).
 *   Opt-in so existing callers keep their prior behaviour.
 * @returns {{ flipUp, flipLeft, theHeight, menuStyle }} — menuStyle is set only
 *   when escapeOverflow is on, and is the fixed-position box for the menu.
 */
export default function useDropdownPosition(containerRef, isOpen, options = {}) {
  const {
    reflowOnScroll = false, boundarySelector = null, escapeOverflow = false,
    // 'left' | 'right' — pin the escaped menu to that edge of widthSelector's
    // element and size it to that element's width less the inset on both sides,
    // instead of matching the trigger's own box.
    align = null,
    // The element whose width the menu takes. Defaults to the trigger.
    widthSelector = null
  } = options;
  const [flipUp, setFlipUp] = useState(false);
  const [flipLeft, setFlipLeft] = useState(false);
  const [theHeight, setHeight] = useState(null);
  const [menuStyle, setMenuStyle] = useState(null);

  /**
   * The element the menu must fit inside.
   *
   * Normally the nearest scrollable ancestor, which is the right answer for a
   * dropdown in a modal or a long form. It is the wrong answer for one sitting
   * in a short scroll container — a table with two rows in it has almost no
   * height, so the menu decides there is no room either way and opens into a
   * few pixels. `boundarySelector` names a bigger element to measure against
   * instead; `escapeOverflow` then takes the menu out of the clipping ancestor
   * so it can actually use that space.
   */
  const findBoundary = useCallback(() => {
    if (boundarySelector) {
      const named = containerRef.current?.closest(boundarySelector);
      if (named) return named;
    }
    return null;
  }, [containerRef, boundarySelector]);

  /**
   * The nearest ancestor that a fixed menu is actually positioned against.
   *
   * Returns its PADDING box, not its border box: that is the containing block
   * CSS uses, and getBoundingClientRect gives the border box. Null means nothing
   * intervenes and the viewport really is the frame.
   */
  const findFixedFrame = useCallback(() => {
    let el = containerRef.current?.parentElement;
    while (el && el !== document.documentElement) {
      const cs = window.getComputedStyle(el);
      if (establishesFixedContainingBlock(cs)) {
        const r = el.getBoundingClientRect();
        return {
          left: r.left + (parseFloat(cs.borderLeftWidth) || 0),
          right: r.right - (parseFloat(cs.borderRightWidth) || 0),
          top: r.top + (parseFloat(cs.borderTopWidth) || 0),
          bottom: r.bottom - (parseFloat(cs.borderBottomWidth) || 0)
        };
      }
      el = el.parentElement;
    }
    return null;
  }, [containerRef]);

  const findScrollParent = useCallback(() => {
    let el = containerRef.current?.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      if (style.overflow === 'auto' || style.overflow === 'scroll' ||
          style.overflowY === 'auto' || style.overflowY === 'scroll' ||
          el.classList.contains('modal-body') ||
          el.classList.contains('modal-content')) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }, [containerRef]);

  // withHeight=false updates only the flip flags (used on scroll so the menu
  // stays a fixed height once opened while still re-choosing its direction).
  const measure = useCallback((withHeight = true) => {
    if (!containerRef.current || !isOpen) return;

    const rect = containerRef.current.getBoundingClientRect();
    const boundary = findBoundary();
    const scrollParent = boundary || findScrollParent();
    const parentRect = scrollParent ? scrollParent.getBoundingClientRect() : null;

    const bottomEdge = parentRect ? parentRect.bottom : window.innerHeight;
    const topEdge = parentRect ? parentRect.top : 0;
    const rightEdge = parentRect ? parentRect.right : window.innerWidth;

    const spaceBelow = bottomEdge - rect.bottom;
    const spaceAbove = rect.top - topEdge;

    // Measure the menu's natural height so (when opted in) we flip up only when
    // the menu would actually overflow the container's bottom edge.
    const menuEl = containerRef.current.querySelector('.dropdown-menu');
    let naturalHeight = null;
    if (menuEl) {
      const prevHeight = menuEl.style.height;
      menuEl.style.height = 'auto';
      naturalHeight = menuEl.scrollHeight;
      menuEl.style.height = prevHeight;
    }

    const needed = reflowOnScroll ? (naturalHeight || 180) : 180;
    const shouldFlipUp = spaceBelow < needed && spaceAbove > spaceBelow;
    setFlipUp(shouldFlipUp);

    if (withHeight) {
      const availableSpace = shouldFlipUp ? spaceAbove : spaceBelow;
      const maxAvailable = Math.max(120, Math.floor(availableSpace - 20));
      setHeight(naturalHeight != null ? (naturalHeight <= maxAvailable ? null : maxAvailable) : null);
    }

    const spaceRight = rightEdge - rect.right;
    setFlipLeft(spaceRight < 150);

    // Fixed positioning is what actually escapes an overflow:hidden ancestor —
    // a fixed box is clipped only by transform/filter/contain ancestors, not by
    // scroll containers. The coordinates come from the trigger, so the menu
    // still reads as belonging to it.
    if (escapeOverflow) {
      const widthEl = widthSelector ? containerRef.current?.closest(widthSelector) : null;
      setMenuStyle(menuBoxFor({
        rect,
        frame: findFixedFrame(),
        flipUp: shouldFlipUp,
        viewportHeight: window.innerHeight,
        viewportWidth: window.innerWidth,
        align,
        widthRect: widthEl ? widthEl.getBoundingClientRect() : null
      }));
    }
  }, [containerRef, isOpen, findScrollParent, findBoundary, findFixedFrame,
      reflowOnScroll, escapeOverflow, align, widthSelector]);

  useEffect(() => {
    if (!isOpen) {
      setFlipUp(false);
      setFlipLeft(false);
      setHeight(null);
      setMenuStyle(null);
      return;
    }

    // Measure once on open (with height). Resize is rare enough to re-measure.
    measure(true);
    window.addEventListener('resize', measure);

    // Opt-in: re-evaluate the flip direction as the scroll ancestor scrolls,
    // keeping the height fixed so the menu doesn't reshape mid-scroll.
    let scrollTarget = null;
    const onScroll = () => measure(false);
    if (reflowOnScroll) {
      scrollTarget = findScrollParent();
      (scrollTarget || window).addEventListener('scroll', onScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('resize', measure);
      if (reflowOnScroll) (scrollTarget || window).removeEventListener('scroll', onScroll);
    };
  }, [isOpen, measure, reflowOnScroll, findScrollParent]);

  return { flipUp, flipLeft, theHeight, menuStyle };
}
